/**
 * 物品/装备数据定义
 */

export type ItemType = 'consumable' | 'equipment' | 'material';
export type EquipmentSlot = 'weapon' | 'armor' | 'accessory' | 'storage';

export interface Item {
  id: string;
  name: string;
  type: ItemType;
  description: string;
  /** 售价（灵石） */
  price: number;
}

export interface Consumable extends Item {
  type: 'consumable';
  hpRestore?: number;
  mpRestore?: number;
  expBonus?: number;
}

export interface Equipment extends Item {
  type: 'equipment';
  slot: EquipmentSlot;
  attackBonus?: number;
  defenseBonus?: number;
  hpBonus?: number;
  mpBonus?: number;
  /** 储物容量（仅储物装备） */
  capacity?: number;
}

export interface Material extends Item {
  type: 'material';
}

export type GameItem = Consumable | Equipment | Material;

/**
 * 预设物品
 */
export const ITEMS: GameItem[] = [
  // 消耗品
  {
    id: 'hp_pill',
    name: '回春丹',
    type: 'consumable',
    description: '恢复 50 点生命值',
    price: 20,
    hpRestore: 50,
  },
  {
    id: 'mp_pill',
    name: '聚灵丹',
    type: 'consumable',
    description: '恢复 30 点灵力值',
    price: 15,
    mpRestore: 30,
  },
  {
    id: 'exp_pill',
    name: '悟道丹',
    type: 'consumable',
    description: '立即获得 100 点修炼经验',
    price: 100,
    expBonus: 100,
  },
  {
    id: 'meridian_pill',
    name: '通脉丹',
    type: 'consumable',
    description: '冲穴时服用，提升20%成功率。经脉修炼者的必备丹药。',
    price: 300,
    hpRestore: 0,
  },
  {
    id: 'enhance_lucky',
    name: '强化幸运符',
    type: 'consumable',
    description: '装备强化时自动消耗，成功率+15%。',
    price: 500,
    hpRestore: 0,
  },
  {
    id: 'enhance_protect',
    name: '强化保护符',
    type: 'consumable',
    description: '强化失败时自动消耗，等级不下降。',
    price: 800,
    hpRestore: 0,
  },
  {
    id: 'enhance_safe',
    name: '装备保护符',
    type: 'consumable',
    description: '+10以上强化失败时自动消耗，装备不损毁。',
    price: 3000,
    hpRestore: 0,
  },
  {
    id: 'meridian_shield',
    name: '护脉丹',
    type: 'consumable',
    description: '冲穴时自动消耗，失败时保护经脉不受反噬伤害。',
    price: 200,
    hpRestore: 0,
  },
  {
    id: 'breakthrough_pill',
    name: '筑基丹',
    type: 'consumable',
    description: '大境界突破时服用，提升30%成功率。突破修仙第一道大坎的必备丹药。',
    price: 300,
    hpRestore: 0,
  },

  // 装备
  {
    id: 'iron_sword',
    name: '铁剑',
    type: 'equipment',
    slot: 'weapon',
    description: '普通铁剑，攻击+5',
    price: 50,
    attackBonus: 5,
  },
  {
    id: 'spirit_sword',
    name: '灵剑',
    type: 'equipment',
    slot: 'weapon',
    description: '蕴含灵力的剑，攻击+15',
    price: 200,
    attackBonus: 15,
  },
  {
    id: 'cloth_armor',
    name: '布衣',
    type: 'equipment',
    slot: 'armor',
    description: '普通布衣，防御+3',
    price: 30,
    defenseBonus: 3,
  },
  {
    id: 'spirit_armor',
    name: '灵甲',
    type: 'equipment',
    slot: 'armor',
    description: '灵力护甲，防御+10',
    price: 150,
    defenseBonus: 10,
  },
  {
    id: 'jade_pendant',
    name: '玉佩',
    type: 'equipment',
    slot: 'accessory',
    description: '温养经脉，HP上限+30',
    price: 80,
    hpBonus: 30,
  },
  {
    id: 'dark_sword',
    name: '玄铁重剑',
    type: 'equipment',
    slot: 'weapon',
    description: '以妖丹淬炼玄铁所铸，削铁如泥。攻击+30',
    price: 0,
    attackBonus: 30,
  },
  {
    id: 'flame_ring',
    name: '烈焰环',
    type: 'equipment',
    slot: 'accessory',
    description: '妖丹火属灵力凝聚而成，佩戴者攻击大增。攻击+12',
    price: 0,
    attackBonus: 12,
  },

  // 材料
  {
    id: 'spirit_herb',
    name: '灵草',
    type: 'material',
    description: '蕴含灵气的草药，可用于炼丹',
    price: 10,
  },
  {
    id: 'monster_core',
    name: '妖丹',
    type: 'material',
    description: '妖兽内丹，炼丹的稀有材料',
    price: 50,
  },

  // ===== 储物装备 =====
  {
    id: 'storage_pouch',
    name: '储物袋',
    type: 'equipment',
    slot: 'storage',
    description: '最基础的储物法宝，修仙界人手一个。可容纳10件物品。',
    price: 50,
    capacity: 10,
  },
  {
    id: 'spirit_pouch',
    name: '灵兽袋',
    type: 'equipment',
    slot: 'storage',
    description: '比储物袋更高级，内部空间更大。可容纳20件物品。',
    price: 200,
    capacity: 20,
  },
  {
    id: 'storage_ring',
    name: '储物戒指',
    type: 'equipment',
    slot: 'storage',
    description: '戴在指上的储物法宝，外观低调但空间惊人。可容纳35件物品。',
    price: 500,
    capacity: 35,
  },
  {
    id: 'qiankun_bag',
    name: '乾坤袋',
    type: 'equipment',
    slot: 'storage',
    description: '传说中的储物至宝，内有乾坤。据说是上古大能的随身洞府碎片所化。可容纳60件物品。',
    price: 0,
    capacity: 60,
  },
];
